import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { API, restoreEntitlement } from "../lib/api";
import { SiteHeader } from "../components/SiteHeader";
import { SomaticTexture } from "../components/SomaticTexture";
import { ArrowLeft, X, ChevronLeft, ChevronRight, Lock, Loader2, LayoutGrid, GalleryHorizontal, KeyRound, Download, BookOpen } from "lucide-react";

const SERIF = '"Cormorant Garamond", Georgia, serif';
const SANS = '"Plus Jakarta Sans", system-ui, sans-serif';
const SPANS = ["col-span-12 sm:col-span-7", "col-span-12 sm:col-span-5", "col-span-12 sm:col-span-5", "col-span-12 sm:col-span-7", "col-span-12 sm:col-span-6", "col-span-12 sm:col-span-6"];

const readTiers = () => {
  try { return JSON.parse(localStorage.getItem("bnm-entitlement") || "{}").tiers || []; }
  catch { return []; }
};
const readCode = () => {
  try { return JSON.parse(localStorage.getItem("bnm-entitlement") || "{}").access_code || ""; }
  catch { return ""; }
};

export default function VaultGallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("salon");
  const [lightbox, setLightbox] = useState(null);
  const [tiers, setTiers] = useState(readTiers());
  const [code, setCode] = useState("");
  const [unlockErr, setUnlockErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const railRef = useRef(null);

  // Admin preview / email links: "?code=" unlocks, "?fresh=1" forces the locked gate.
  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("fresh") === "1") {
        localStorage.removeItem("bnm-entitlement");
        setTiers([]);
        window.history.replaceState(null, "", window.location.pathname);
        return;
      }
      const c = sp.get("code");
      if (c) {
        restoreEntitlement(c).then((data) => {
          localStorage.setItem("bnm-entitlement", JSON.stringify(data));
          setTiers(data.tiers || []);
          window.history.replaceState(null, "", window.location.pathname);
        }).catch(() => {});
      }
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasBook = tiers.includes("book_owner");
  const hasVault = tiers.includes("vault_member");
  const fullAccess = hasBook && hasVault;

  useEffect(() => {
    const c = readCode();
    const url = c ? `${API}/gallery?code=${encodeURIComponent(c)}` : `${API}/gallery`;
    axios.get(url).then(({ data }) => setItems(data.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, [tiers]);

  const plates = useMemo(() => items.filter((i) => i.kind !== "cover"), [items]);
  const teaserPlates = useMemo(() => items.filter((i) => i.kind === "art").slice(0, 5), [items]);

  const close = useCallback(() => setLightbox(null), []);
  const step = useCallback((dir) => {
    setLightbox((cur) => (cur == null ? cur : Math.min(plates.length - 1, Math.max(0, cur + dir))));
  }, [plates.length]);

  useEffect(() => {
    if (lightbox == null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, close, step]);

  const unlock = async (e) => {
    e.preventDefault();
    setUnlockErr("");
    setSubmitting(true);
    try {
      const data = await restoreEntitlement(code);
      localStorage.setItem("bnm-entitlement", JSON.stringify(data));
      setTiers(data.tiers || []);
      setCode("");
    } catch (err) {
      setUnlockErr(err.message || "That access code wasn't found.");
    } finally {
      setSubmitting(false);
    }
  };

  const active = lightbox != null ? plates[lightbox] : null;

  if (loading) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center" style={{ background: "#0C0E12" }}>
        <Loader2 className="h-6 w-6 animate-spin text-[#D4A373]" />
      </div>
    );
  }

  const Shell = ({ children }) => (
    <div className="relative min-h-[100dvh]" style={{ background: "#0C0E12", fontFamily: SANS }} data-testid="vault-gallery-page">
      <SomaticTexture />
      <SiteHeader theme="dark" right={fullAccess ? (
        <div className="flex items-center gap-1 rounded-full p-1" style={{ background: "rgba(255,255,255,0.05)" }}>
          <button type="button" onClick={() => setView("salon")} data-testid="vault-gallery-view-salon"
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-colors ${view === "salon" ? "text-[#0C0E12]" : "text-[#A3A8B2]"}`}
            style={view === "salon" ? { background: "#D4A373" } : {}}>
            <LayoutGrid className="h-3.5 w-3.5" /> Salon
          </button>
          <button type="button" onClick={() => setView("rail")} data-testid="vault-gallery-view-rail"
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-colors ${view === "rail" ? "text-[#0C0E12]" : "text-[#A3A8B2]"}`}
            style={view === "rail" ? { background: "#D4A373" } : {}}>
            <GalleryHorizontal className="h-3.5 w-3.5" /> Rail
          </button>
        </div>
      ) : null} />
      {!fullAccess && teaserPlates.length > 0 && (
        <section className="relative z-10 mx-auto max-w-6xl px-5 pt-10 sm:px-8" data-testid="vault-gallery-teaser">
          <p className="text-center text-[11px] uppercase tracking-[0.35em]" style={{ color: "#98A898" }}>Echoes of the form · a glimpse of the archive</p>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-5 sm:gap-4">
            {teaserPlates.map((p, i) => (
              <motion.button
                key={p.index}
                type="button"
                onClick={() => setLightbox(plates.findIndex((x) => x.index === p.index))}
                data-testid={`vault-gallery-teaser-plate-${i}`}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.08 * i, ease: [0.22, 1, 0.36, 1] }}
                className="group relative aspect-[3/4] overflow-hidden rounded-sm ring-1 ring-white/10"
              >
                <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]" style={{ filter: "brightness(0.74) saturate(1.02)" }} />
                <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(to top, rgba(12,14,18,0.82), transparent 55%)" }} />
                <p className="pointer-events-none absolute inset-x-0 bottom-0 px-2.5 pb-2 text-left text-[12px] leading-tight" style={{ fontFamily: SERIF, color: "#F7F5F0" }}>{p.title}</p>
              </motion.button>
            ))}
          </div>
          <div className="mt-5 text-center">
            <Link to="/gallery/preview" data-testid="vault-gallery-teaser-link" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.25em] transition-colors hover:text-[#F7F5F0]" style={{ color: "#D4A373" }}>
              <GalleryHorizontal className="h-3.5 w-3.5" /> View the full preview
            </Link>
          </div>
        </section>
      )}
      {children}
    </div>
  );

  // ---- Gate card (shared) ----
  const GateCard = ({ testid, icon: Icon, kicker, title, body, cta, codeHint }) => (
    <section className="relative z-10 mx-auto flex min-h-[70vh] max-w-2xl flex-col justify-center px-5 py-16 sm:px-8" data-testid={testid}>
      <div className="relative overflow-hidden rounded-sm p-8 text-center sm:p-12" style={{ background: "#12151C", border: "1px solid rgba(212,163,115,0.2)" }}>
        <div className="pointer-events-none absolute inset-0 opacity-60" style={{ background: "radial-gradient(70% 60% at 50% 0%, rgba(212,163,115,0.12), transparent 70%)" }} />
        <span className="relative mx-auto flex h-12 w-12 items-center justify-center rounded-full" style={{ background: "rgba(212,163,115,0.12)", color: "#D4A373" }}><Icon className="h-5 w-5" /></span>
        <p className="relative mt-5 text-[11px] uppercase tracking-[0.35em]" style={{ color: "#98A898" }}>{kicker}</p>
        <h2 className="relative mt-3 text-3xl sm:text-4xl" style={{ fontFamily: SERIF, color: "#F7F5F0" }}>{title}</h2>
        <p className="relative mx-auto mt-4 max-w-md text-sm leading-relaxed" style={{ color: "#A3A8B2" }}>{body}</p>
        {cta}
        <form onSubmit={unlock} className="relative mx-auto mt-6 flex max-w-xs items-center gap-2">
          <input value={code} onChange={(e) => { setCode(e.target.value); if (unlockErr) setUnlockErr(""); }} placeholder={codeHint} data-testid="vault-gallery-code-input"
            className="min-w-0 flex-1 rounded-full border bg-transparent px-4 py-2 text-sm text-[#F7F5F0] placeholder:text-[#636A78] focus:outline-none"
            style={{ borderColor: "rgba(255,255,255,0.12)" }} />
          <button type="submit" disabled={!code.trim() || submitting} data-testid="vault-gallery-code-submit" className="inline-flex items-center gap-1 rounded-full px-3 py-2 text-xs disabled:opacity-40" style={{ background: "rgba(255,255,255,0.08)", color: "#D4A373" }}>
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />}
          </button>
        </form>
        {unlockErr && <p className="relative mt-2 text-xs text-[#C88A75]" data-testid="vault-gallery-code-error">{unlockErr}</p>}
      </div>
    </section>
  );

  // ---- 1) Free reader → book paywall ----
  if (!hasBook) {
    return (
      <Shell>
        <GateCard
          testid="vault-gallery-gate-book"
          icon={BookOpen}
          kicker="A room beyond the book"
          title="The full Art Gallery lives past Chapter One"
          body="Every somatic plate and its words wait inside the complete volume. Carry the book forward to step into the exhibition."
          codeHint="Have an access code?"
          cta={(
            <Link to="/checkout?product=braced-no-more-book" data-testid="vault-gallery-get-book"
              className="relative mt-6 inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 text-sm font-medium transition-transform hover:scale-[1.02]"
              style={{ background: "#D4A373", color: "#0C0E12" }}>
              Carry the complete volume — $33.99
            </Link>
          )}
        />
      </Shell>
    );
  }

  // ---- 2) Book owner → vault upsell ----
  if (!hasVault) {
    return (
      <Shell>
        <GateCard
          testid="vault-gallery-gate-vault"
          icon={Lock}
          kicker="One more door"
          title="The full gallery opens with the Language Vault"
          body="You carry the book — thank you. The complete exhibition, every painting and reflection, is held inside the Language Vault alongside monthly somatic reframes and audio from Casey."
          codeHint="Member access code"
          cta={(
            <Link to="/offerings/the-language-vault" data-testid="vault-gallery-enter-vault"
              className="relative mt-6 inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 text-sm font-medium transition-transform hover:scale-[1.02]"
              style={{ background: "#D4A373", color: "#0C0E12" }}>
              Enter the Language Vault
            </Link>
          )}
        />
      </Shell>
    );
  }

  // ---- 3) Full access ----
  const PlateCard = ({ p, i, className = "" }) => (
    <motion.button
      type="button"
      onClick={() => setLightbox(i)}
      data-testid={`vault-gallery-tile-${p.index}`}
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative block overflow-hidden rounded-sm text-left ${className}`}
      style={{ background: "#1A1E27", boxShadow: "0 20px 60px -30px rgba(0,0,0,0.9)" }}
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img src={p.image} alt={p.title} loading="lazy"
          className="h-full w-full object-cover transition-[transform,filter] duration-[1200ms] ease-out group-hover:scale-[1.04]"
          style={{ filter: "brightness(0.82) saturate(1.02)" }} />
      </div>
      <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-700 group-hover:opacity-100"
        style={{ background: "radial-gradient(120% 90% at 50% 0%, rgba(212,163,115,0.18), transparent 60%)" }} />
      <div className="p-5">
        <p className="text-[10px] uppercase tracking-[0.3em]" style={{ color: "#98A898", fontFamily: SANS }}>Plate {String(p.index - 2).padStart(2, "0")}</p>
        <p className="mt-2 text-2xl leading-tight" style={{ fontFamily: SERIF, color: "#F7F5F0" }}>{p.title}</p>
        <p className="mt-1.5 text-sm leading-relaxed" style={{ fontFamily: SANS, color: "#A3A8B2" }}>{p.caption}</p>
      </div>
    </motion.button>
  );

  return (
    <Shell>
      <section className="relative z-10 mx-auto max-w-6xl px-5 pb-10 pt-16 sm:px-8 sm:pt-24">
        <div className="pointer-events-none absolute left-1/2 top-0 h-72 w-[46rem] max-w-[90vw] -translate-x-1/2"
          style={{ background: "radial-gradient(60% 100% at 50% 0%, rgba(212,163,115,0.16), transparent 70%)" }} />
        <p data-testid="vault-gallery-meta" className="relative text-[11px] uppercase tracking-[0.35em]" style={{ color: "#98A898" }}>The Full Exhibition · {plates.filter((p) => p.kind === "art").length} Somatic Plates</p>
        <h1 data-testid="vault-gallery-title" className="relative mt-4 text-5xl leading-[0.95] sm:text-6xl lg:text-7xl" style={{ fontFamily: SERIF, color: "#F7F5F0", fontWeight: 500 }}>
          The Full Art Gallery
        </h1>
        <p className="relative mt-5 max-w-xl text-lg italic" style={{ fontFamily: SERIF, color: "#D4A373" }}>Unlocked for you — paintings &amp; words by Casey Muze.</p>
        <p className="relative mt-5 max-w-lg text-sm leading-relaxed" style={{ color: "#A3A8B2" }}>
          Enter slowly. This is not a presentation — it is a room your nervous system has been waiting for. Take one breath before you continue.
        </p>
      </section>

      <section className="relative z-10">
        {view === "salon" ? (
          <div className="mx-auto max-w-6xl px-5 pb-20 sm:px-8">
            <div className="grid grid-cols-12 gap-4 sm:gap-6">
              {plates.map((p, i) => (
                <div key={p.index} className={SPANS[i % SPANS.length]}>
                  <PlateCard p={p} i={i} className="h-full" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div ref={railRef} className="flex snap-x snap-mandatory gap-6 overflow-x-auto px-5 pb-20 sm:px-8"
            style={{ scrollbarWidth: "thin" }} data-testid="vault-gallery-rail">
            {plates.map((p, i) => (
              <div key={p.index} className="w-[82vw] flex-none snap-center sm:w-[520px]">
                <PlateCard p={p} i={i} />
              </div>
            ))}
          </div>
        )}
      </section>

      <AnimatePresence>
        {active && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8" style={{ background: "rgba(8,9,12,0.96)", backdropFilter: "blur(8px)" }}
            onClick={close} data-testid="vault-gallery-lightbox">
            <button type="button" onClick={close} aria-label="Close" data-testid="vault-lightbox-close"
              className="absolute right-4 top-4 rounded-full p-2 text-[#A3A8B2] transition-colors hover:text-white" style={{ background: "rgba(255,255,255,0.06)" }}>
              <X className="h-5 w-5" />
            </button>
            {lightbox > 0 && (
              <button type="button" onClick={(e) => { e.stopPropagation(); step(-1); }} aria-label="Previous" data-testid="vault-lightbox-prev"
                className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full p-2.5 text-[#A3A8B2] transition-colors hover:text-white sm:left-6" style={{ background: "rgba(255,255,255,0.06)" }}>
                <ChevronLeft className="h-6 w-6" />
              </button>
            )}
            {lightbox < plates.length - 1 && (
              <button type="button" onClick={(e) => { e.stopPropagation(); step(1); }} aria-label="Next" data-testid="vault-lightbox-next"
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-2.5 text-[#A3A8B2] transition-colors hover:text-white sm:right-6" style={{ background: "rgba(255,255,255,0.06)" }}>
                <ChevronRight className="h-6 w-6" />
              </button>
            )}
            <motion.div key={active.index} initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="grid max-h-full w-full max-w-5xl items-center gap-6 lg:grid-cols-[1.4fr_1fr]" onClick={(e) => e.stopPropagation()}>
              <img src={active.image} alt={active.title} className="max-h-[72vh] w-full rounded-sm object-contain" style={{ boxShadow: "0 40px 120px -40px rgba(0,0,0,1)" }} />
              <div className="text-left">
                <p className="text-[10px] uppercase tracking-[0.3em]" style={{ color: "#98A898" }}>Plate {String(active.index - 2).padStart(2, "0")}</p>
                <p className="mt-3 text-3xl leading-tight" style={{ fontFamily: SERIF, color: "#F7F5F0" }}>{active.title}</p>
                <p className="mt-4 text-base italic leading-relaxed" data-testid="vault-lightbox-caption" style={{ fontFamily: SERIF, color: "#D4A373" }}>{active.caption}</p>
                {active.kind === "art" && (
                  <a href={active.image} download={`braced-no-more-plate-${String(active.index - 2).padStart(2, "0")}.jpg`} data-testid="vault-lightbox-download-print"
                    className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-transform hover:scale-[1.02]"
                    style={{ background: "rgba(212,163,115,0.14)", color: "#E0A96D", border: "1px solid rgba(212,163,115,0.3)" }}>
                    <Download className="h-4 w-4" /> Download print
                  </a>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Shell>
  );
}
